import React, { useState, useEffect, Suspense } from 'react'
import { Loader, Button, DatasetTableTypes, Skeleton } from '@stratascratch/ui'
import type { Table } from '@stratascratch/ui/components/DatasetTableTypes'
import { Eye, Database } from '@stratascratch/icons'
import { executeCode } from '../lib/api'
import { TableData } from '@stratascratch/code-editor'
import {
  container,
  tableNameLabel,
  previewContainer,
  resultsTable,
  typesTable,
  buttonsContainer,
  button,
} from './DatasetPreview.module.css'

const ResultsTable = React.lazy(async () => {
  const mod = await import('@stratascratch/code-editor')
  return { default: mod.ResultsTable }
})

const LIMIT = 5

interface Props {
  value: {
    questionId: number | undefined
    tableName: string | undefined
    table: Table
    preview: TableData | undefined
    questionClass?: string
  }
}

/**
 * We display initially 5 rows, which are queried server side (during build).
 * The data layer returns max 6 rows. By checking whether there's a 6th row, we know whether there is more data to fetch and set hasMore to true
 */
function DatasetPreview({ value }: Props) {
  const { questionId, tableName, table, preview, questionClass } = value

  const [tableData, setTableData] = useState<TableData | undefined>(preview)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(
    !!preview && preview.data?.length > LIMIT,
  )
  const [showTypes, setShowTypes] = useState(false)

  const queryTable = async () => {
    if (!tableName || !questionId) return

    setLoading(true)
    // For Visual Questions, use Python (code_type: 2)
    // For Coding Questions, use PgSQL (code_type: 1)
    const codeType = questionClass === 'Visual Question' ? 2 : 1

    const response = await executeCode({
      question_id: questionId,
      code: `SELECT * FROM ${tableName}`,
      check_solution: false,
      code_type: codeType,
    })

    const results = response?.results

    if (results) {
      // Ensuring that the results conform to the TableData interface
      const formattedResults: TableData = {
        index: results.index || [],
        data: results.data || [],
        columns: results.columns || [],
      }
      setTableData(formattedResults)
    }

    setHasMore(false)
    setLoading(false)
  }

  useEffect(() => {
    // If the preview data changes (e.g., when switching between question types), update the state
    setTableData(preview)
    setHasMore(!!preview && preview.data?.length > LIMIT)
  }, [preview])

  if (!table) return <div>Missing data</div>

  const onShowAll = async () => {
    if (questionClass === 'Visual Question') {
      // For Visual Questions, all data is already fetched during build
      setTableData(preview)
      setHasMore(false)
      return
    }
    await queryTable()
  }

  const slicedResults =
    hasMore && tableData
      ? {
          ...tableData,
          index: tableData.index.slice(0, LIMIT),
          data: tableData.data.slice(0, LIMIT),
        }
      : tableData

  const isSSR = typeof window === 'undefined'

  return (
    <div className={container}>
      <span className={tableNameLabel}>
        <span className="font-bold">Table:</span> {tableName}
      </span>
      <div className={previewContainer}>
        {slicedResults && !isSSR ? (
          <Suspense fallback={<div>Loading Dataset</div>}>
            <ResultsTable
              results={slicedResults}
              className={`${resultsTable} my-4 max-h-96 grow overflow-y-auto`}
            />
          </Suspense>
        ) : (
          <Skeleton className="h-96" />
        )}
        {showTypes && table.columns && (
          <DatasetTableTypes className={typesTable} table={table} />
        )}
      </div>
      <div className={buttonsContainer}>
        <Button
          variant="outline"
          size="small"
          onClick={onShowAll}
          disabled={
            loading || (!hasMore && questionClass !== 'Visual Question')
          }
          className={button}
        >
          <Eye />
          Show all {loading && <Loader />}
        </Button>
        <Button
          variant={showTypes ? 'primary' : 'outline'}
          size="small"
          onClick={() => {
            setShowTypes(!showTypes)
          }}
          className={button}
          disabled={!table}
        >
          <Database />
          Toggle dTypes
        </Button>
      </div>
    </div>
  )
}

export default DatasetPreview
